

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useSubscription } from '@/hooks/useSubscription';

interface AIGenerationButtonProps {
  fieldIdToLabel: Record<string, string>; // Mapping from field UUID to label
  section: string;
  fields: string[]; // Field IDs to generate
  context?: Record<string, unknown>;
  variant?: 'button' | 'icon';
}

export function AIGenerationButton({
  fieldIdToLabel,
  section,
  fields,
  context = {},
  variant = 'button'
}: AIGenerationButtonProps) {
  const { hasFeature, isTokenDepleted, loading: subLoading } = useSubscription();
  const { backend } = useFirebase();
  const { selectedNovelId } = useNovel();
  const { getValues, setValue } = useFormContext();

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [guidelines, setGuidelines] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // If subscription is loading or user doesn't have the feature, don't render
  if (subLoading || !hasFeature('ai_assistance')) {
    return null;
  }

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const showNotification = (message: string, severity: 'success' | 'info' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setGuidelines('');
  };

  const handleGenerate = async () => {
    if (!backend) {
      showNotification('Firebase backend not initialized', 'error');
      return;
    }

    if (!selectedNovelId) {
      showNotification('No novel selected', 'error');
      return;
    }

    setLoading(true);
    try {
      const character = getValues();

      // Transform character data: replace UUID keys with labels
      const labeledCharacter: Record<string, unknown> = {};
      const emptyFields: string[] = [];

      Object.entries(character).forEach(([key, value]) => {
        // Exclude internal/image metadata fields from context and generation
        if (['id', 'ownerId', 'novelId', 'images', 'pendingFiles', 'mainImageUrl', 'mainImagePath', 'primaryImageId', 'audit', 'eventChanges'].includes(key)) {
          return;
        }

        const label = fieldIdToLabel[key] || key;
        labeledCharacter[label] = value;

        // Track empty fields
        if (!value || value === '') {
          emptyFields.push(label);
        }
      });

      // Map requested fields from IDs to labels, ensuring internal fields are excluded
      const labeledFields = fields
        .filter(fieldId => !['id', 'images', 'pendingFiles', 'mainImageUrl', 'mainImagePath', 'primaryImageId', 'audit', 'eventChanges'].includes(fieldId))
        .map(fieldId => fieldIdToLabel[fieldId] || fieldId);

      // Build reverse mapping: label → field ID
      // Include both custom fields (UUID → label) and core fields (name → name)
      const labelToFieldId: Record<string, string> = {};
      Object.entries(fieldIdToLabel).forEach(([id, label]) => {
        labelToFieldId[label] = id;
      });
      // Add identity mappings for core fields (not in fieldIdToLabel)
      fields.forEach(fieldId => {
        const label = fieldIdToLabel[fieldId] || fieldId;
        if (!labelToFieldId[label]) {
          labelToFieldId[label] = fieldId;
        }
      });

      const data = await backend.ai.generateCharacterFields({
        novelId: selectedNovelId,
        character: { ...labeledCharacter, ...context },
        section,
        fields: labeledFields,
        emptyFields, // Provide all empty fields for context
        guidelines: guidelines || undefined, // Add user guidelines
      });

      if (data && data.suggestions) {
        console.warn('✨ AI Suggestions received:', data.suggestions);
        console.warn('🔄 Reverse mapping:', labelToFieldId);
        console.warn('📋 Requested fields:', fields);

        Object.entries(data.suggestions).forEach(([label, value]) => {
          // Map label back to field ID
          const fieldId = labelToFieldId[label] || label;

          console.warn(`Processing: "${label}" → "${fieldId}"`, {
            inRequested: fields.includes(fieldId),
            value
          });

          // Only update if field was requested (security/safety)
          if (fields.includes(fieldId)) {
            setValue(fieldId, value, { shouldDirty: true, shouldValidate: true });
            console.warn(`✓ Updated: ${fieldId}`);
          } else {
            console.warn(`✗ Skipped: ${fieldId} (not requested)`);
          }
        });
        showNotification('Generated successfully!', 'success');
        handleCloseDialog(); // Close dialog on success
      } else {
        showNotification('No suggestions generated.', 'info');
      }

    } catch (error) {
      console.error('AI Generation failed:', error);
      showNotification('Failed to generate content. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const content = variant === 'icon' ? (
    <Tooltip title={isTokenDepleted ? 'AI tokens exhausted for this period' : 'Generate with AI'}>
      <span>
        <IconButton
          onClick={handleOpenDialog}
          disabled={loading || isTokenDepleted}
          color="primary"
          size="small"
        >
          {loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={isTokenDepleted ? 'AI tokens exhausted for this period' : ''}>
      <span>
        <Button
          onClick={handleOpenDialog}
          disabled={loading || isTokenDepleted}
          startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          variant="outlined"
          size="small"
          sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          {loading ? 'Generating...' : 'Generate AI'}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <>
      {content}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>AI Generation Guidelines</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Guidelines for Character"
            placeholder="e.g., Make them mysterious, with a tragic past..."
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Generating...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
